import { useQuery } from '@tanstack/react-query'
import { api } from '../api'
import { MarketPanel } from './model/MarketPanel'
import { RecordPanel } from './model/RecordPanel'
import { VersionsPanel } from './model/VersionsPanel'
import { Tabs } from './ui/Tabs'

/**
 * Panel order, and the first one is the default.
 *
 * Versions leads: the first thing to establish about a published probability is whose it is and what
 * it was allowed to be judged on. A record read without knowing which model produced it, and whether
 * that model had already seen the matches, is the exact mistake this site once shipped — 108 fits to
 * training data published as an 83% prediction record. So provenance comes first and the scores come
 * second, which is the order the argument runs in.
 */
const PANELS = [
  { key: 'versions', label: 'Versions' },
  { key: 'record', label: 'Record' },
  { key: 'market', label: 'Versus the market' },
] as const

type Panel = (typeof PANELS)[number]['key']

const isPanel = (value: string | undefined): value is Panel =>
  PANELS.some((panel) => panel.key === value)

/**
 * What the model is, how well it has done, and whether it beats the market.
 *
 * **Three questions, three panels, and the split is the point.** This was one page of nine sections
 * running to nine thousand pixels: the headline record, a reliability chart, two breakdowns, the
 * current model's card, a champion-versus-shadow comparison, the full version history, and the
 * caveats. A reader who wanted to know how good the model is scrolled past its biography; a reader
 * who wanted the biography scrolled past the record; and there was no way to link to either.
 *
 * Adding the market comparison — which is the largest of the three and the one the site's whole
 * claim rests on — to the bottom of that scroll would have made it worse in the same direction. So
 * the page now navigates:
 *
 * - **Versions** — whose numbers are these, what was each allowed to be judged on, and what did each
 *   score? First, because a record means nothing until you know which model produced it.
 * - **Record** — are the published numbers true? Calibration first, accuracy beside it.
 * - **Versus the market** — are they better than a bookmaker's? The question that decides whether
 *   there is an edge at all, and the one the Record panel's own caveat has been arguing on theory.
 *
 * The sub-tab lives in the URL fragment (`#/model/market`), which `route.ts` already carries
 * segments for, so a panel can be linked to and a reload keeps you where you were.
 *
 * Nothing here is cached server-side. Every figure is recomputed from stored predictions and stored
 * prices on each load, so the page cannot drift from the record it claims to describe.
 */
export function ModelPage({
  leagueId,
  segments = [],
  go,
}: {
  leagueId?: number
  /** Everything after `model` in the fragment. Absent means the default panel. */
  segments?: string[]
  go?: (panel: string) => void
}) {
  // An unrecognised segment resolves to the default panel rather than to an empty page, so a stale
  // link lands somewhere useful. Read from PANELS rather than named again here: the default and the
  // first tab are one decision, and writing it twice is how they come apart.
  const panel: Panel = isPanel(segments[0]) ? segments[0] : PANELS[0].key

  const model = useQuery({ queryKey: ['model'], queryFn: api.model })
  const stats = useQuery({
    queryKey: ['stats', leagueId ?? 'all'],
    queryFn: () => api.stats(leagueId),
    refetchInterval: 120_000,
  })

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-2xl font-semibold">How this model is doing</h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-soft">
          Every probability the site has published is stored with the model version that produced it
          and can never be rewritten. These are the scores on that record — not on a backtest.
        </p>
      </section>

      <Tabs
        ariaLabel="What to look at"
        variant="underline"
        active={panel}
        onChange={(next) => go?.(next)}
        tabs={[...PANELS]}
      />

      {/*
        The market panel fetches its own data and does not wait on these two, which is deliberate:
        it takes a bookmaker and a comparison point that nothing else on the page cares about, and
        blocking it behind the record's queries would make switching book feel like a page load.
      */}
      {panel === 'market' ? (
        <MarketPanel leagueId={leagueId} />
      ) : model.isPending || stats.isPending ? (
        <Skeleton />
      ) : model.error || stats.error ? (
        <p className="rounded border border-danger-edge bg-danger-bg px-4 py-3 text-sm text-danger">
          Could not load the model record.
        </p>
      ) : panel === 'versions' ? (
        <VersionsPanel
          card={model.data}
          headToHead={stats.data.headToHead}
          models={stats.data.models ?? []}
        />
      ) : (
        <RecordPanel stats={stats.data} />
      )}
    </div>
  )
}

function Skeleton() {
  return (
    <div className="space-y-8" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="h-28 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />
        <div className="h-28 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />
      </div>
      <div className="h-60 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />
    </div>
  )
}
